/**
 * The owner of a durable write's `settled` when its caller never took it: a
 * durable verb (a writable facade's `writeDurable` / `clearDurable`, `row.updateDurable`,
 * `sheets.updateDurable`, `events.updateDurable`, `themes.setDurable`)
 * returns its `Durable` through `observedDurable`, which watches `settled`.
 * A caller that reads `settled` owns it (awaits it, or attaches its
 * report); a refusal of a write whose `settled` nobody read is handed to the
 * client's `ClientConfig.requestErrorHandler` — never left an unhandled
 * rejection. The handler is bound to the client's lifecycle
 * (`bindUnobservedRefusals`, by the client); a verb of a facade built
 * without a client traces the refusal (`net`).
 *
 * @internal SDK-private: no barrel exports it.
 */
import type { Option } from "@tento-lona/sheets";
import { SdkRequestError } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import type { Durable } from "./durable-handoffs.ts";
import { type ClientLifecycle, unobservedRefusalsOf } from "./handle-errors.ts";

/**
 * `durable` with its `settled` watched: reading `settled` (a getter) marks
 * it observed, and the caller owns it; a rejection of a `settled` nobody
 * read by the time it rejects goes to the handler bound to `owner` (the
 * owning client's lifecycle; `null`: none) — an `SdkRequestError` as is,
 * anything else (a disposed client's refusal) traced. A handler that throws
 * (`RequestErrorHandlers.THROW`) makes the refusal an unhandled rejection,
 * as it was before.
 */
export function observedDurable<T, S>(owner: Option<ClientLifecycle>, durable: Durable<T, S>): Durable<T, S> {
  const settled = durable.settled;
  let observed = false;
  settled.then(undefined, (err: unknown) => {
    if (observed) return;
    reportUnobserved(owner, err);
  });
  return Object.freeze({
    applied: durable.applied,
    get settled(): Promise<S> {
      observed = true;
      return settled;
    },
  });
}

/** Hand one unobserved refusal to `owner`'s handler (see `observedDurable`). */
function reportUnobserved(owner: Option<ClientLifecycle>, err: unknown): void {
  const handler = owner == null ? null : unobservedRefusalsOf(owner);
  if (handler != null && err instanceof SdkRequestError) {
    handler(err);
    return;
  }
  Tracing.warn("net")(`a durable write was refused and nobody read its settled: ${String(err)}`);
}
